'use client';

/* ──────────────────────────────────────────────
 *  useCustomFieldAnswers — saisie des champs personnalisés
 *  (réponses, erreurs « requis », validation). Partagé par le
 *  tunnel classique (CheckoutStep) et le tunnel course (RideTunnel).
 * ────────────────────────────────────────────── */

import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import type { BookingCustomField, CustomFieldAnswer } from '../../types';
import {
  filterVisibleCustomFields,
  isCustomFieldAnswerMissing,
  upsertCustomFieldAnswer,
} from '../../utils/custom-fields';

export interface UseCustomFieldAnswersParams {
  /** Tous les champs actifs du pro (filtrés ici selon les prestations ET les lieux réservés). */
  fields: BookingCustomField[];
  /** Prestations réservées (référence stable attendue — mémoïsée par l'appelant). */
  prestationIds: readonly number[];
  /**
   * Lieux réservés : ceux des créneaux envoyés au serveur (tunnel classique : le lieu choisi).
   * VIDE pour une course (aucun lieu — `NO_BOOKED_PLACE_IDS`) : un champ restreint à des lieux ne
   * s'y applique jamais. Référence stable attendue — mémoïsée par l'appelant.
   */
  placeIds: readonly number[];
  /** Message affiché sous un champ requis non renseigné. */
  requiredLabel: string;
}

export interface UseCustomFieldAnswersResult {
  /**
   * Champs applicables à la réservation (prestations ET lieux), triés par `sort_order` — SOURCE
   * UNIQUE : affichage (`DynamicFormFields`), validation « requis » et réponses envoyées.
   */
  visibleFields: BookingCustomField[];
  answers: CustomFieldAnswer[];
  errors: Record<number, string>;
  /** Remplace les réponses (ex. restauration d'une saisie après rechargement de la page). */
  setAnswers: Dispatch<SetStateAction<CustomFieldAnswer[]>>;
  updateAnswer: (fieldId: number, value: string | number | boolean) => void;
  /** Contrôle « requis » d'un champ quittant le focus (erreur posée ou levée). */
  blurField: (fieldId: number) => void;
  /** Valide tous les champs requis applicables : pose les erreurs, `false` si incomplet. */
  validate: () => boolean;
}

export function useCustomFieldAnswers({
  fields,
  prestationIds,
  placeIds,
  requiredLabel,
}: UseCustomFieldAnswersParams): UseCustomFieldAnswersResult {
  const [answers, setAnswers] = useState<CustomFieldAnswer[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});

  const prestationIdSet = useMemo((): ReadonlySet<number> => new Set(prestationIds), [prestationIds]);
  const placeIdSet = useMemo((): ReadonlySet<number> => new Set(placeIds), [placeIds]);
  const visibleFields = useMemo(
    (): BookingCustomField[] =>
      filterVisibleCustomFields(fields, { prestationIds: prestationIdSet, placeIds: placeIdSet }),
    [fields, prestationIdSet, placeIdSet],
  );

  const updateAnswer = useCallback((fieldId: number, value: string | number | boolean): void => {
    setAnswers((prev): CustomFieldAnswer[] => upsertCustomFieldAnswer(prev, fieldId, value));
  }, []);

  const blurField = useCallback(
    (fieldId: number): void => {
      const field = visibleFields.find((f): boolean => f.custom_field_id === fieldId);
      if (!field?.required) return;
      const missing = isCustomFieldAnswerMissing(answers.find((a): boolean => a.custom_field_id === fieldId));
      setErrors((prev): Record<number, string> => {
        const updated = { ...prev };
        if (missing) updated[fieldId] = requiredLabel;
        else delete updated[fieldId];
        return updated;
      });
    },
    [visibleFields, answers, requiredLabel],
  );

  const validate = useCallback((): boolean => {
    const nextErrors: Record<number, string> = {};
    for (const field of visibleFields) {
      if (!field.required) continue;
      const answer = answers.find((a): boolean => a.custom_field_id === field.custom_field_id);
      if (isCustomFieldAnswerMissing(answer)) nextErrors[field.custom_field_id] = requiredLabel;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [visibleFields, answers, requiredLabel]);

  return { visibleFields, answers, errors, setAnswers, updateAnswer, blurField, validate };
}
